export const STATUS_TONES = ['neutral', 'info', 'success', 'warning', 'danger', 'action'] as const
export type StatusTone = (typeof STATUS_TONES)[number]

export const CHIP_TONES = [...STATUS_TONES, 'authority'] as const
export type ChipTone = (typeof CHIP_TONES)[number]

export const COLOR_TOKENS = [
  'ink',
  'ink-muted',
  'ink-faint',
  'ink-inverse',
  'paper',
  'paper-raised',
  'paper-sunken',
  'paper-inverse',
  'paper-inverse-ink',
  'rule',
  'rule-soft',
  'rule-strong',
  'machine',
  'machine-ink',
  'machine-soft',
  'action',
  'action-strong',
  'action-ink',
  'action-soft',
  'commit',
  'commit-ink',
  'danger',
  'warn',
  'ok',
  'hatch',
  'focus',
  'overlay',
  'shadow-sheet',
  ...CHIP_TONES.map((tone) => `tone-${tone}-ink` as const),
  ...CHIP_TONES.map((tone) => `tone-${tone}-wash` as const),
  ...CHIP_TONES.map((tone) => `tone-${tone}-solid` as const),
  ...CHIP_TONES.map((tone) => `tone-${tone}-rule` as const),
  ...CHIP_TONES.map((tone) => `tone-${tone}-tint` as const),
  'tone-authority-contrast',
] as const

export const SPACE_TOKENS = ['s-1', 's-2', 's-3', 's-4', 's-5', 's-6', 'radius', 'radius-sheet', 'gutter', 'gutter-lg', 'measure'] as const

export const TYPE_TOKENS = [
  'font-body',
  'font-mono',
  'font-display',
  'type-scale',
  'type-step-xsmall',
  'type-step-small',
  'type-step-reduced',
  'type-step-large',
  'type-step-xlarge',
  't-floor',
  't-sample',
  't-display',
  't-h1',
  't-h2',
  't-message',
  't-body',
  't-ui',
  't-meta',
  't-mono',
  'lh-display',
  'lh-h1',
  'lh-h2',
  'lh-message',
  'lh-body',
  'lh-ui',
  'lh-meta',
  'lh-mono',
  'tracking',
  'tracking-tight',
  'tracking-caps',
  'weight-regular',
  'weight-medium',
  'weight-bold',
] as const

export const DENSITY_TOKENS = [
  'target-scale',
  'tap-min',
  'control-h',
  'control-h-lg',
  'control-min-w',
  'field-h',
  'choice-h',
  'chip-h',
  'tab-h',
  'tab-marker',
  'current-marker',
  'row-h',
  'ledger-lead-w',
  'meter-bar-w',
  'meter-bar-h',
  'meter-gap',
  'deadline-bar-w',
  'deadline-bar-h',
  'appbar-h',
  'nav-h',
  'rail-w',
  'icon-rail-w',
  'avatar-size',
  'icon-inline',
  'icon-mark',
  'bar-h',
  'hatch-h',
  'pane-list-w',
  'pane-aside-w',
  'pane-main-max',
  'page-max',
] as const

export const TEXT_TOKENS = ['t-display', 't-h1', 't-h2', 't-message', 't-body', 't-ui', 't-meta', 't-mono'] as const

export const TARGET_TOKENS = ['tap-min', 'control-h', 'control-h-lg', 'control-min-w', 'field-h', 'tab-h'] as const

export const SCALED_TOKENS = [
  ...TEXT_TOKENS,
  ...TARGET_TOKENS,
  'choice-h',
  'chip-h',
  'row-h',
  'appbar-h',
  'nav-h',
  'rail-w',
  'avatar-size',
  'icon-inline',
  'icon-mark',
] as const

export const LEGACY_TOKENS = [
  'ground',
  'ground-sunken',
  'ink-soft',
  'ink-ghost',
  'paper-muted',
  'accent',
  'accent-ink',
  'accent-wash',
  'irreversible',
  'irreversible-ink',
  'machine-wash',
  'shadow-overlay',
  'radius-lg',
  'touch-min',
  'text-xs',
  'text-sm',
  'text-base',
  'text-lg',
  'text-xl',
  'text-2xl',
  'leading-tight',
  'leading-body',
  'space-1',
  'space-2',
  'space-3',
  'space-4',
  'space-5',
  'space-6',
] as const

export const toneClass = (tone: ChipTone): string => `tone-${tone}`

export const textToneClass = (tone: ChipTone): string => `text-tone ${toneClass(tone)}`
